import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { LENS_SECTIONS, LENS_ASSIGNEE_ME, lensShows, lensIsActive, lensSectionsActive, lensPointsActive, lensVisibleCount, lensOnly, lensTags, lensToggleTag, lensWithTags, lensAssignee, lensWithAssignee } from '../lensSections';
import TtsAttemptsChip from './TtsAttemptsChip';
import QuestMenu from './QuestMenu';
import MainMenu from './MainMenu';

// Funnel glyph for the Lens button — a filter, not a search.
const FunnelIcon = () => (
  <svg className="lens-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M1.6 2.2h12.8L9.6 8.1v4.7l-3.2 1.9V8.1L1.6 2.2z"
      fill="currentColor"
    />
  </svg>
);

const QuestSelector = ({ quests, onQuestSelect, selectedQuestId, saveStatus, onLogout, onEditQuestInfo, onEditLocations, onShowAudioStats, onShowTagsSummary, onShowAudioStudio, onShowDiffViewer, selectedTable, onTableSelect, isReadOnly, onCopyQuestJson, onExportPdf, onExportMarkdown, currentUser, onShowAdminPanel, onCreateQuest, onImportQuest, onShowPipeline, onShowPlatformListings, onShowProcesses, onShowTasks, onShowImageUpscaler, onShowPromptBuilder, navHrefs = {}, pipelineMode = false, lens = {}, onLensChange, availableTags = [], availableAssignees = [] }) => {
  const isAdmin = !!(currentUser && currentUser.isAdmin);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuButtonRef = useRef(null);

  // Lens popover (view filter for location-card sections). Rendered in a body
  // portal with fixed positioning so it sits above the map/other stacking
  // contexts; position is computed from the button on open.
  const [lensOpen, setLensOpen] = useState(false);
  const [lensPos, setLensPos] = useState(null);
  const lensButtonRef = useRef(null);
  const lensRef = useRef(null);
  const lensActive = lensIsActive(lens);
  const sectionsActive = lensSectionsActive(lens);
  const pointsActive = lensPointsActive(lens);
  const pickedTags = lensTags(lens);
  const pickedAssignee = lensAssignee(lens);
  const currentUsername = currentUser && currentUser.username;
  const toggleSection = (key) => onLensChange && onLensChange({ ...lens, [key]: !lensShows(lens, key) });
  const toggleTag = (tag) => onLensChange && onLensChange(lensToggleTag(lens, tag));
  // Clicking the active recipient again clears the choice (back to "Anyone").
  const pickAssignee = (who) =>
    onLensChange && onLensChange(lensWithAssignee(lens, pickedAssignee === who ? null : who));
  const toggleLensOpen = () => {
    if (lensOpen) { setLensOpen(false); return; }
    const r = lensButtonRef.current && lensButtonRef.current.getBoundingClientRect();
    if (r) setLensPos({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
    setLensOpen(true);
  };

  // Theme toggle (light/dark). The actual <html data-theme> attribute is the
  // source of truth (set pre-paint in index.html); this state just mirrors it.
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-theme') === 'dark'
  );

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
    try {
      localStorage.setItem('theme', next ? 'dark' : 'light');
    } catch (e) {}
  };

  // Ctrl+K / Cmd+K opens the menu with the cursor in its search box; pressed
  // again while the menu is open, it just puts the cursor back there.
  const [menuFocusToken, setMenuFocusToken] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.code === 'KeyK') {
        e.preventDefault();
        setMenuOpen(true);
        setMenuFocusToken((t) => t + 1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close the lens popover when clicking outside it.
  useEffect(() => {
    const onDown = (e) => {
      if (lensButtonRef.current && !lensButtonRef.current.contains(e.target) &&
          lensRef.current && !lensRef.current.contains(e.target)) {
        setLensOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleMenuItemClick = (action) => {
    if (action === 'logout' && onLogout) {
      onLogout();
    } else if (action === 'edit-info' && onEditQuestInfo) {
      onEditQuestInfo();
    } else if (action === 'edit-locations' && onEditLocations) {
      onEditLocations();
    } else if (action === 'show-audio-stats' && onShowAudioStats) {
      onShowAudioStats();
    } else if (action === 'show-audio-studio' && onShowAudioStudio) {
      onShowAudioStudio();
    } else if (action === 'show-tags-summary' && onShowTagsSummary) {
      onShowTagsSummary();
    } else if (action === 'copy-quest-json' && onCopyQuestJson) {
      onCopyQuestJson();
    } else if (action === 'export-pdf' && onExportPdf) {
      onExportPdf();
    } else if (action === 'export-markdown' && onExportMarkdown) {
      onExportMarkdown();
    } else if (action === 'show-diff' && onShowDiffViewer) {
      onShowDiffViewer();
    } else if (action === 'show-admin' && onShowAdminPanel) {
      onShowAdminPanel();
    } else if (action === 'show-pipeline' && onShowPipeline) {
      onShowPipeline();
    } else if (action === 'show-platforms' && onShowPlatformListings) {
      onShowPlatformListings();
    } else if (action === 'show-processes' && onShowProcesses) {
      onShowProcesses();
    } else if (action === 'show-tasks' && onShowTasks) {
      onShowTasks();
    } else if (action === 'show-image-upscaler' && onShowImageUpscaler) {
      onShowImageUpscaler();
    } else if (action === 'show-prompt-builder' && onShowPromptBuilder) {
      onShowPromptBuilder();
    } else if (action === 'create-quest' && onCreateQuest) {
      onCreateQuest();
    } else if (action === 'import-quest' && onImportQuest) {
      onImportQuest();
    }
    setMenuOpen(false);
  };

  const getStatusIndicatorClass = () => {
    switch (saveStatus) {
      case 'saving':
        return 'status-indicator saving';
      case 'success':
        return 'status-indicator success';
      case 'error':
        return 'status-indicator error';
      default:
        return 'status-indicator idle';
    }
  };

  // The menu tree. Leaves fire the same actions as before via their key;
  // `keywords` only feed the search box. Groups open as a flyout (wide) or in
  // place (narrow) — see MainMenu.
  const withSelect = (it) => (it.sep
    ? it
    : it.items
      ? { ...it, items: it.items.map(withSelect) }
      : { ...it, onSelect: () => handleMenuItemClick(it.key) });
  const selectedQuest = (quests || []).find((q) => q.quest_id === selectedQuestId);
  const noQuestHint = selectedQuestId ? undefined : 'Pick a quest first';
  const menuItems = [
    {
      key: 'quest', label: 'Quest', icon: '📍', sub: selectedQuest ? selectedQuest.quest_name : null,
      items: [
        { key: 'edit-info', label: 'Quest Info', href: navHrefs.info, keywords: ['edit', 'description', 'settings', 'price', 'cover'] },
        { key: 'edit-locations', label: 'Locations', href: navHrefs.editor, keywords: ['edit', 'points', 'map', 'editor', 'cards'] },
        { key: 'show-audio-studio', label: 'Audio Studio', href: navHrefs.studio, disabled: !selectedQuestId, title: noQuestHint, keywords: ['tts', 'voice', 'sound', 'narration'] },
        { key: 'show-tasks', label: 'Tasks', icon: '✅', href: navHrefs.tasks, disabled: !selectedQuestId, title: noQuestHint || "Remarks and tasks of this quest, with the Autoexecutor's results", keywords: ['remarks', 'todo', 'autoexecutor', 'comments'] },
        { key: 'show-diff', label: 'Compare with Prod', icon: '⇆', disabled: !selectedQuestId || selectedTable === 'quests', title: selectedTable === 'quests' ? 'Switch to Test environment to compare' : (noQuestHint || 'Compare Test version with Production'), keywords: ['diff', 'test', 'production', 'compare'] },
      ],
    },
    {
      key: 'export', label: 'Export', icon: '📤',
      items: [
        { key: 'copy-quest-json', label: 'Copy Quest JSON', icon: '📋', disabled: !selectedQuestId, title: noQuestHint, keywords: ['clipboard', 'json', 'copy'] },
        { key: 'export-pdf', label: 'Export to PDF', icon: '📄', disabled: !selectedQuestId, title: noQuestHint, keywords: ['print', 'pdf'] },
        { key: 'export-markdown', label: 'Export to Markdown', icon: '📝', disabled: !selectedQuestId, title: noQuestHint, keywords: ['md', 'text'] },
      ],
    },
    {
      key: 'overview', label: 'Overview', icon: '🗂',
      items: [
        { key: 'show-audio-stats', label: 'Audio Stats', href: navHrefs.audioStats, keywords: ['tts', 'statistics', 'voice', 'duration'] },
        { key: 'show-tags-summary', label: 'Tags Summary', href: navHrefs.tags, keywords: ['tags', 'labels', 'assigned'] },
        { key: 'show-platforms', label: 'Platform Listings', icon: '🤝', href: navHrefs.platforms, title: 'How each prod quest maps to its Sputnik / Tripster listing (read by the auto-answer bot)', keywords: ['sputnik', 'tripster', 'listings', 'partners', 'bot'] },
        { key: 'show-pipeline', label: 'Quest Pipeline', icon: '🧩', href: navHrefs.pipeline, keywords: ['generate', 'auto', 'llm', 'runs'] },
      ],
    },
    {
      key: 'tools', label: 'Tools', icon: '🔧',
      items: [
        { key: 'show-image-upscaler', label: 'Image Upscaler', icon: '🖼', keywords: ['upscale', 'picture', 'resolution', 'enhance'] },
        { key: 'show-prompt-builder', label: 'Prompt Builder', icon: '🧾', keywords: ['prompt', 'llm', 'template'] },
      ],
    },
    isAdmin && {
      key: 'admin', label: 'Admin', icon: '👤',
      items: [
        { key: 'show-admin', label: 'Admin Panel', keywords: ['users', 'permissions', 'access'] },
        { key: 'show-processes', label: 'Processes', icon: '⚙️', href: navHrefs.processes, title: 'Background processes behind long runs: who runs, heartbeat, Stop / Kill, logs', keywords: ['background', 'runners', 'logs', 'kill', 'stop'] },
      ],
    },
    { sep: true },
    { key: 'create-quest', label: 'New Quest', icon: '➕', accent: true, keywords: ['add', 'create'] },
    { key: 'import-quest', label: 'Import JSON', icon: '📥', accent: true, keywords: ['upload', 'file', 'json'] },
    { sep: true },
    { key: 'logout', label: 'Logout', muted: true, keywords: ['sign out', 'exit'] },
  ].filter(Boolean).map(withSelect);

  // Log the quests data for debugging
  console.log("Available quests:", quests);
  console.log("Selected quest ID:", selectedQuestId);

  return (
    <div className="quest-selector">
      <div className="selector-container">
        <div className="menu-container">
          <button 
            className="menu-button" 
            title="Menu"
            onClick={() => setMenuOpen(!menuOpen)}
            ref={menuButtonRef}
          >
            ☰
          </button>
          <MainMenu
            open={menuOpen}
            onClose={closeMenu}
            anchorRef={menuButtonRef}
            items={menuItems}
            focusToken={menuFocusToken}
          />
        </div>
        {pipelineMode ? (
          <div className="selector-main-content">
            <span style={{ fontWeight: 600, opacity: 0.8 }}>🧩 Quest Pipeline</span>
          </div>
        ) : (
        <div className="selector-main-content">
          <div className={getStatusIndicatorClass()} title={`Status: ${saveStatus}`}></div>

          {isAdmin && (
            <div className="selector-group">
              <label htmlFor="table-select" className="selector-group-label">Env</label>
              <select
                id="table-select"
                value={selectedTable}
                onChange={(e) => onTableSelect(e.target.value)}
                className="table-select-dropdown"
              >
                <option value="quests_test">Test</option>
                <option value="quests">Production</option>
              </select>
            </div>
          )}

          <div className="selector-group selector-group-quest">
            <label htmlFor="quest-select" className="selector-group-label">Quest</label>
            <QuestMenu quests={quests} value={selectedQuestId} onSelect={onQuestSelect} hrefFor={navHrefs.quest} />
          </div>
          {isReadOnly && <span className="read-only-indicator" title="You are in read-only mode.">🔒 Read Only</span>}
        </div>
        )}
        <div className="lens-control">
          <button
            ref={lensButtonRef}
            className={`lens-button ${lensActive ? 'active' : ''}`}
            onClick={toggleLensOpen}
            title="Lens — show only the sections and the tagged points you pick"
            aria-label="Lens view filter"
          >
            <FunnelIcon />
            {sectionsActive && (
              <span className="lens-badge" title={`${lensVisibleCount(lens)} of ${LENS_SECTIONS.length} sections shown`}>
                {lensVisibleCount(lens)}
              </span>
            )}
            {pointsActive && (
              <span
                className="lens-badge lens-badge-tags"
                title={[
                  pickedTags.length > 0 ? `Tags: ${pickedTags.join(', ')}` : null,
                  pickedAssignee ? `Assigned to: ${pickedAssignee === LENS_ASSIGNEE_ME ? (currentUsername || 'me') : pickedAssignee}` : null,
                ].filter(Boolean).join(' · ')}
              >
                {pickedTags.length > 0 ? `#${pickedTags.length}` : ''}{pickedAssignee ? '@' : ''}
              </span>
            )}
          </button>
          {lensOpen && lensPos && ReactDOM.createPortal(
            <div className="lens-popover" ref={lensRef} style={{ top: lensPos.top, right: lensPos.right }}>
              <div className="lens-popover-head">
                <span>Lens</span>
                <button
                  className="lens-showall"
                  disabled={!lensActive}
                  onClick={() => onLensChange && onLensChange({})}
                >
                  Show all
                </button>
              </div>
              <div className="lens-group-label">Card sections</div>
              {LENS_SECTIONS.map((s) => (
                <div key={s.key} className="lens-row">
                  <label className="lens-row-label">
                    <input
                      type="checkbox"
                      checked={lensShows(lens, s.key)}
                      onChange={() => toggleSection(s.key)}
                    />
                    <span>{s.label}</span>
                  </label>
                  <button
                    className="lens-only"
                    title={`Show only ${s.label}`}
                    onClick={() => onLensChange && onLensChange(lensOnly(s.key))}
                  >
                    Only
                  </button>
                </div>
              ))}
              {/* Point filter: keep only the points carrying one of the picked
                  tags (OR logic, same as Tags Summary) and, optionally, only
                  those where such a tag is addressed to one given person.
                  Points that fall out leave the list and go pale + unclickable
                  on the map. */}
              <div className="lens-group-label lens-group-label-tags">
                <span>Points by tag (OR)</span>
                {pointsActive && (
                  <button
                    className="lens-showall"
                    onClick={() => onLensChange && onLensChange(lensWithAssignee(lensWithTags(lens, []), null))}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="lens-tag-chips">
                {availableTags.map(({ tag, count }) => {
                  const on = pickedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      className={`lens-tag-chip ${on ? 'active' : ''} ${count === 0 ? 'unused' : ''}`}
                      onClick={() => toggleTag(tag)}
                      title={
                        count === 0
                          ? `No point of this quest is tagged "${tag}"`
                          : on
                            ? `Stop filtering by "${tag}"`
                            : `Show only the ${count} point${count === 1 ? '' : 's'} tagged "${tag}"`
                      }
                    >
                      {on ? '✓ ' : ''}{tag}
                      <span className="lens-tag-count">{count}</span>
                    </button>
                  );
                })}
              </div>
              {/* Whom the tag is addressed to. Scoped to the tags picked above,
                  so "waiting for review" + Masha means exactly that tag, not
                  any other tag she happens to own on the same point. */}
              <div className="lens-group-label lens-group-label-assignee">Assigned to</div>
              <div className="lens-tag-chips">
                <button
                  className={`lens-tag-chip ${!pickedAssignee ? 'active' : ''}`}
                  onClick={() => onLensChange && onLensChange(lensWithAssignee(lens, null))}
                  title="Do not filter by recipient"
                >
                  {!pickedAssignee ? '✓ ' : ''}Anyone
                </button>
                {currentUsername && (
                  <button
                    className={`lens-tag-chip ${pickedAssignee === LENS_ASSIGNEE_ME ? 'active' : ''}`}
                    onClick={() => pickAssignee(LENS_ASSIGNEE_ME)}
                    title={`Show only points whose tag is addressed to ${currentUsername}`}
                  >
                    {pickedAssignee === LENS_ASSIGNEE_ME ? '✓ ' : ''}Me
                  </button>
                )}
                {availableAssignees.map((u) => (
                  <button
                    key={u}
                    className={`lens-tag-chip ${pickedAssignee === u ? 'active' : ''}`}
                    onClick={() => pickAssignee(u)}
                    title={`Show only points whose tag is addressed to ${u}`}
                  >
                    {pickedAssignee === u ? '✓ ' : ''}{u}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
        </div>
        <TtsAttemptsChip />
        <button
          className="theme-toggle-button"
          onClick={toggleTheme}
          title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label="Toggle theme"
        >
          {isDark ? '☀️' : '🌙'}
        </button>
      </div>
    </div>
  );
};

export default QuestSelector; 